import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  useTheme,
  Chip,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Button,
  Alert,
} from '@mui/material';
import {
  Timer as TimerIcon,
  Restaurant as RestaurantIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as DeliveryIcon,
  Store as PickupIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';
import LocationSelector from '../components/LocationSelector';
import BrandSelector from '../components/BrandSelector';

interface Order {
  id: string;
  orderNumber: string;
  type: 'pickup' | 'delivery' | 'dine-in';
  status: 'pending' | 'preparing' | 'ready' | 'completed';
  items: number;
  total: number;
  orderTime: Date;
  promiseTime: Date;
  actualTime?: Date;
}

interface KitchenMetrics {
  avgPrepTime: number;
  ordersInQueue: number;
  staffActive: number;
  efficiency: number;
}

const Operations: React.FC = () => {
  const theme = useTheme();
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isLive, setIsLive] = useState(true);

  // Mock real-time data
  const [orders, setOrders] = useState<Order[]>([
    {
      id: '1',
      orderNumber: '#2451',
      type: 'pickup',
      status: 'preparing',
      items: 3,
      total: 24.99,
      orderTime: new Date(Date.now() - 5 * 60000),
      promiseTime: new Date(Date.now() + 10 * 60000),
    },
    {
      id: '2',
      orderNumber: '#2452',
      type: 'delivery',
      status: 'pending',
      items: 5,
      total: 42.50,
      orderTime: new Date(Date.now() - 2 * 60000),
      promiseTime: new Date(Date.now() + 25 * 60000),
    },
    {
      id: '3',
      orderNumber: '#2453',
      type: 'dine-in',
      status: 'ready',
      items: 2,
      total: 18.75,
      orderTime: new Date(Date.now() - 8 * 60000),
      promiseTime: new Date(Date.now() - 1 * 60000),
    },
  ]);

  const [kitchenMetrics] = useState<KitchenMetrics>({
    avgPrepTime: 7.5,
    ordersInQueue: 12,
    staffActive: 6,
    efficiency: 92,
  });

  // Simulate real-time updates
  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      setLastUpdate(new Date());
      // Update order statuses randomly
      setOrders(prevOrders => 
        prevOrders.map(order => {
          if (Math.random() > 0.8 && order.status !== 'completed') {
            const statuses: Order['status'][] = ['pending', 'preparing', 'ready', 'completed'];
            const currentIndex = statuses.indexOf(order.status);
            return {
              ...order,
              status: statuses[Math.min(currentIndex + 1, statuses.length - 1)],
              actualTime: order.status === 'ready' ? new Date() : order.actualTime,
            };
          }
          return order;
        })
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [isLive]);

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return theme.palette.warning.main;
      case 'preparing': return theme.palette.info.main;
      case 'ready': return theme.palette.success.main;
      case 'completed': return theme.palette.text.secondary;
      default: return theme.palette.text.primary;
    }
  };

  const getOrderTypeIcon = (type: Order['type']) => {
    switch (type) {
      case 'pickup': return <PickupIcon />;
      case 'delivery': return <DeliveryIcon />;
      case 'dine-in': return <RestaurantIcon />;
    }
  };

  const calculateTimeRemaining = (promiseTime: Date) => {
    const diff = promiseTime.getTime() - Date.now();
    const minutes = Math.floor(diff / 60000);
    return minutes;
  };

  const ordersByStatus = {
    pending: orders.filter(o => o.status === 'pending').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    ready: orders.filter(o => o.status === 'ready').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Operations Dashboard
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={isLive ? 'LIVE' : 'PAUSED'}
                size="small"
                sx={{
                  backgroundColor: isLive ? theme.palette.success.main : theme.palette.warning.main,
                  color: 'white',
                  fontWeight: 600,
                  animation: isLive ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.6 },
                    '100%': { opacity: 1 },
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Last update: {lastUpdate.toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant={isLive ? 'outlined' : 'contained'}
              onClick={() => setIsLive(!isLive)}
              startIcon={<RefreshIcon />}
            >
              {isLive ? 'Pause' : 'Resume'}
            </Button>
            <Box sx={{ minWidth: 250 }}>
              <BrandSelector value={selectedBrand} onChange={(brand) => setSelectedBrand(brand as string)} />
            </Box>
            <Box sx={{ minWidth: 300 }}>
              <LocationSelector value={selectedLocation} onChange={setSelectedLocation} selectedBrand={selectedBrand} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(175, 35, 38, 0.2)' }}>
                  <TimerIcon sx={{ color: theme.palette.primary.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Avg Prep Time
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {kitchenMetrics.avgPrepTime} min
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                    <Typography variant="caption" sx={{ color: theme.palette.success.main }}>
                      -12% from last hour
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(33, 150, 243, 0.2)' }}>
                  <RestaurantIcon sx={{ color: theme.palette.info.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Orders in Queue
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {kitchenMetrics.ordersInQueue}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip label={`${ordersByStatus.pending} pending`} size="small" />
                    <Chip label={`${ordersByStatus.preparing} preparing`} size="small" />
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(76, 175, 80, 0.2)' }}>
                  <SpeedIcon sx={{ color: theme.palette.success.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Kitchen Efficiency
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {kitchenMetrics.efficiency}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={kitchenMetrics.efficiency}
                    sx={{
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: theme.palette.success.main,
                      },
                    }}
                  />
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.4 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ backgroundColor: 'rgba(255, 152, 0, 0.2)' }}>
                  <WarningIcon sx={{ color: theme.palette.warning.main }} />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Late Orders
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {orders.filter(o => calculateTimeRemaining(o.promiseTime) < 0 && o.status !== 'completed').length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Requires immediate attention
                  </Typography>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Live Orders Table */}
      <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.5 }}>
        <Card sx={{ ...glassStyle, p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Live Order Tracking
          </Typography>
          
          <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Time Remaining</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {orders.map((order) => {
                    const timeRemaining = calculateTimeRemaining(order.promiseTime);
                    const isLate = timeRemaining < 0 && order.status !== 'completed';
                    
                    return (
                      <motion.tr
                        key={order.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        style={{
                          backgroundColor: isLate ? 'rgba(255, 87, 34, 0.1)' : 'transparent',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getOrderTypeIcon(order.type)}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {order.orderNumber}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={order.type} size="small" />
                        </TableCell>
                        <TableCell>{order.items}</TableCell>
                        <TableCell>${order.total.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{
                              backgroundColor: `${getStatusColor(order.status)}20`,
                              color: getStatusColor(order.status),
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {order.status === 'completed' ? (
                            <Typography variant="body2" color="text.secondary">
                              Completed
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {isLate ? (
                                <WarningIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />
                              ) : (
                                <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  color: isLate ? theme.palette.error.main : 'text.primary',
                                  fontWeight: isLate ? 600 : 400,
                                }}
                              >
                                {Math.abs(timeRemaining)} min {isLate ? 'late' : 'left'}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined">
                            View Details
                          </Button>
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </motion.div>

      {/* Alerts Section */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Kitchen Station 2 running 15% slower than average
          </Typography>
        </Alert>
        <Alert severity="info">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Lunch rush expected in 45 minutes based on historical patterns
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

export default Operations;